import {
  Box,
  Typography,
  Container,
  Paper,
  Button,
  TextField,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Email,
  Phone,
  LocationOn,
  ExpandMore,
  BusinessCenter,
  VideoCall,
  Send,
  Support,
  VerifiedUser,
} from "@mui/icons-material";
import { useState } from "react";
import SimpleCaptcha from "../components/SimpleCaptcha";
import axios from "axios";
import Input from "../components/Input";

export default function Contact() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    subject: "",
    message: "",
    inquiryType: "",
  });

  const [expanded, setExpanded] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [captcha, setCaptcha] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);

  function handleChange(panel) {
    return (event, isExpanded) => {
      setExpanded(isExpanded ? panel : false);
    };
  }

  function handleInputChange(e) {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();

    // Validate CAPTCHA
    if (captchaInput.toLowerCase() !== captcha.toLowerCase()) {
      setSnackbar({
        open: true,
        message: "CAPTCHA is incorrect. Please try again.",
        severity: "error",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post('/api/contact/submit', formData);

      // Show success message
      setSnackbar({
        open: true,
        message: response.data.message || "Your message has been sent! We'll get back to you soon.",
        severity: "success",
      });

      // Reset form
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        company: "",
        subject: "",
        message: "",
        inquiryType: "",
      });
      setCaptchaInput('');

    } catch (error) {
      console.error('Form submission error:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to send message. Please try again later.",
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleCloseSnackbar() {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  }

  return (
    <Container>
      <Box
        className="inner-content"
        sx={{
          py: 5,
        }}
      >
        {/* Hero Section */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: "bold",
              fontSize: { xs: "2rem", md: "3rem" },
            }}
          >
            Get in Touch
          </Typography>
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{ mb: 2, maxWidth: 700, mx: "auto" }}
          >
            We're here to help with your EUDR compliance needs
          </Typography>
        </Box>

        {/* Contact Form and Info Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 4,
            mb: 8,
          }}
        >
          {/* Contact Form */}
          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 66.67%" } }}>
            <Paper elevation={1} sx={{ p: { xs: 3, md: 4 } }}>
              <Typography variant="h4" sx={{ mb: 3, fontWeight: "bold" }}>
                Send us a Message
              </Typography>

              <form onSubmit={handleSubmit}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 2,
                    }}
                  >
                    <Input
                      label="First Name"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      fullWidth
                      required
                      margin="normal"
                    />
                    <Input
                      label="Last Name"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      fullWidth
                      required
                      margin="normal"
                    />
                  </Box>
                  <Input
                    label="Email Address"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    margin="normal"
                  />
                  <Input
                    label="Company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    fullWidth
                    margin="normal"
                  />
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Type of Inquiry</InputLabel>
                    <Select
                      name="inquiryType"
                      value={formData.inquiryType}
                      onChange={handleInputChange}
                      label="Type of Inquiry"
                    >
                      <MenuItem value="general">General Question</MenuItem>
                      <MenuItem value="sales">Sales Inquiry</MenuItem>
                      <MenuItem value="support">Technical Support</MenuItem>
                      <MenuItem value="billing">Billing Question</MenuItem>
                      <MenuItem value="partnership">
                        Partnership Opportunity
                      </MenuItem>
                    </Select>
                  </FormControl>
                  <Input
                    label="Subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    margin="normal"
                  />
                  <Input
                    label="Your Message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    multiline
                    rows={4}
                    fullWidth
                    required
                    margin="normal"
                  />
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Security Verification
                    </Typography>
                    <SimpleCaptcha
                      onGenerate={setCaptcha}
                      value={captchaInput}
                      onChange={setCaptchaInput}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      startIcon={<Send />}
                      disabled={isSubmitting}
                      sx={{ mt: 2, px: 4, py: 1.5 }}
                    >
                      {isSubmitting ? "Sending..." : "Send Message"}
                    </Button>
                  </Box>
                </Box>
              </form>
            </Paper>
          </Box>

          {/* Contact Information */}
          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 33.33%" } }}>
            <Card sx={{ mb: 4, height: "100%" }}>
              <CardContent>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: "bold" }}>
                  Contact Information
                </Typography>

                <List>
                  <ListItem sx={{ pl: 0 }}>
                    <ListItemIcon>
                      <Email color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Email"
                      secondary="info@eudr-api.eu"

                    />
                  </ListItem>                 
                  <ListItem sx={{ pl: 0 }}>
                    <ListItemIcon>
                      <Support color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Support"
                      secondary="support@eudr-api.eu"

                    />
                  </ListItem>
                  <ListItem sx={{ pl: 0 }}>
                    <ListItemIcon>
                      <BusinessCenter color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Sales"
                      secondary="sales@eudr-api.eu" 
                    />
                  </ListItem>
                  {/* <ListItem sx={{ pl: 0 }}>
                    <ListItemIcon>
                      <LocationOn color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Office Address"
                      secondary="10000 Zagreb, Croatia"
                    />
                  </ListItem> */}

                  <ListItem divider />
                  {/* Add video call link instructions - can be arrange on request */}
                  <ListItem sx={{ pl: 0 }}>
                    <ListItemIcon>
                      <VideoCall color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Video Call"
                      secondary="If you are interested in pro or enterprise plans, we can arrange a video call to discuss your needs. Contact us to schedule a call."
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Department Cards */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h4"
            sx={{ mb: 4, fontWeight: "bold", textAlign: "center" }}
          >
            Our Teams
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 3,
            }}
          >
            <Box sx={{ flex: "1 1 33.33%" }}>
              <Card sx={{ height: "100%" }}>
                <CardContent sx={{ p: 3, textAlign: "center" }}>
                  <Support
                    sx={{ fontSize: 48, color: "primary.main", mb: 2 }}
                  />
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ fontWeight: "bold" }}
                  >
                    Customer Support
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Technical assistance and troubleshooting for API integration
                    issues
                  </Typography>
                  <Typography
                    component="a"
                    href="mailto:support@eudr-api.com"
                    color="primary"
                  >
                    support@eudr-api.com
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ flex: "1 1 33.33%" }}>
              <Card sx={{ height: "100%" }}>
                <CardContent sx={{ p: 3, textAlign: "center" }}>
                  <BusinessCenter
                    sx={{ fontSize: 48, color: "primary.main", mb: 2 }}
                  />
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ fontWeight: "bold" }}
                  >
                    Sales Team
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Information about our products, pricing plans, and
                    enterprise solutions
                  </Typography>
                  <Typography
                    component="a"
                    href="mailto:sales@eudr-api.com"
                    color="primary"
                  >
                    sales@eudr-api.com
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ flex: "1 1 33.33%" }}>
              <Card sx={{ height: "100%" }}>
                <CardContent sx={{ p: 3, textAlign: "center" }}>
                  <VerifiedUser
                    sx={{ fontSize: 48, color: "primary.main", mb: 2 }}
                  />
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ fontWeight: "bold" }}
                  >
                    Compliance Experts
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Guidance on EUDR regulations and compliance requirements
                  </Typography>
                  <Typography
                    component="a"
                    href="mailto:support@eudr-api.eu"
                    color="primary"
                  >
                    support@eudr-api.eu
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Box>

        {/* FAQs */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h4"
            sx={{ mb: 4, fontWeight: "bold", textAlign: "center" }}
          >
            Frequently Asked Questions
          </Typography>

          <Accordion
            expanded={expanded === "panel1"}
            onChange={handleChange("panel1")}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography fontWeight="medium">
                How quickly can I get started with the API?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>
                You can sign up and start using our API within minutes. After
                registering for a free account, you'll receive your API key and
                can immediately begin making test requests. Our comprehensive
                documentation will guide you through the integration process.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expanded === "panel2"}
            onChange={handleChange("panel2")}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography fontWeight="medium">
                Do you offer custom integration support?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>
                Yes, we provide custom integration support for Enterprise
                customers. Our technical team can work with you to ensure smooth
                integration with your existing systems. For Professional and
                Free plan users, we offer documentation and email support to
                assist with your integration needs.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expanded === "panel3"}
            onChange={handleChange("panel3")}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography fontWeight="medium">
                What is your typical response time for support inquiries?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>
                Our response times vary by plan level. Enterprise customers
                receive priority support with responses within 8 hours.
                Professional plan users can expect responses within 24 hours,
                while Free plan users typically receive responses within 48
                hours during business days.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expanded === "panel4"}
            onChange={handleChange("panel4")}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography fontWeight="medium">
                Can you help with EUDR compliance questions?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>
                While our primary focus is providing API services for EUDR
                compliance, our team includes compliance experts who can offer
                general guidance. For specific legal advice, we recommend
                consulting with regulatory specialists. Enterprise customers
                have access to our compliance team for more detailed assistance.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Call to Action */}
        <Box
          sx={{
            bgcolor: "background.paper",
            p: 6,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ fontWeight: "bold" }}>
            Ready to simplify your EUDR compliance?
          </Typography>
          <Typography sx={{ mb: 4 }}>
            Start using our API today or contact our sales team to discuss your
            specific needs
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              size="large"
              sx={{ px: 4, py: 1.5 }}
              href="/register"
            >
              Get Started
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="large"
              sx={{ px: 4, py: 1.5 }}
              href="mailto:sales@eudr-api.com"
            >
              Email Sales
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
